import { getMatcher } from './getRouteMatchParser';

import { IRouter } from './IRouter';
import { IRouterState } from './IRouterState';
import { getRoute } from './getRoute';
import { IRouteChangedDetail } from './IRouteChangedDetail';
import { IParams } from './IParams';
import { toSearch } from './toSearch';
import { IRouteMatch } from './IRouteMatch';
import { wait } from '../promises';
import { IRouteMatches } from './IRouteMatches';

export function createRouter(
  routes: { [name: string]: string },
  options?: {
    transitionMs: number
  }
): IRouter {

  options = {
    transitionMs: 250,
    ...options
  };

  const state: IRouterState = {
    path: null,
    routes: [],
    matches: null,
    getMatch: null
  };

  const router = async (scrollTop?: number) => {

    const path = {
      name: location.pathname,
      search: location.search
    };

    state.routes = Object.keys(routes)
      .map(name => getRoute({
        name,
        path: routes[name]
      }));

    if (state.path?.name != path.name
      || state.path?.search != path.search) {

      const matches: IRouteMatches = state.routes
        .reduce((m, r) => {
          if (r.match != null) m[r.id] = r.match

          return m;
        }, {} as { [k: string]: IRouteMatch });

      const eventDetail: IRouteChangedDetail = {
        path,
        matches,
        getMatchParam: name => Object
          .values(matches)
          .find(m => m.params?.[name] !== undefined)
          ?.params[name] as string[]
      };

      if (!emitRouteChanging(eventDetail)) return;

      if (state.path?.name != path.name) {
        emitRouting(true);

        await wait(options.transitionMs);

        emitRouting(false);

        const canonical = document.querySelector('link[rel=canonical]');
        if (canonical) canonical.setAttribute('href', location.origin + path.name);

        if (scrollTop != null) window.scrollTo(0, scrollTop);
      }

      state.path = path;
      state.matches = matches;
      state.getMatch = getMatcher(matches);

      emitRouteChanged(eventDetail);
    }
  };

  router.state = state;

  router.go = (path: string | number, replace: boolean = false) => {
    const historyState = { top: Math.round(document.scrollingElement!.scrollTop) };
    history.replaceState(historyState, '', location.href);

    if (typeof path === 'number') {
      history.go(path);

      return;
    }

    if (replace) {

      history.replaceState(historyState, '', path);
    }
    else {

      history.pushState({}, '', path);
    }

    router(0);
  }

  router.back = () => {
    history.back();
  }

  router.search = (params: IParams) => {

    const href = state.path!.name
      + (params
        ? toSearch(params)
        : state.path!.search
      );

    const historyState = { top: Math.round(document.scrollingElement!.scrollTop) };
    history.replaceState(historyState, '', href);

    router();
  }

  router.href = (path: string, replace: boolean = false) => {

    return {
      href: path,
      ref: (el: HTMLElement) => {
        if (!el?.toggleAttribute) return;

        el.toggleAttribute('is-active',
          !!state.routes.find(r => r.pathExp?.test(path))?.match
        );
      },
      onClick: (e: MouseEvent) => {
        if (
          e.button !== 0
          || e.ctrlKey
          || e.metaKey
          || e.shiftKey
        ) return;

        e.preventDefault();

        router.go(path, replace);
      }
    };
  };

  router.onClick = (path: string, replace: boolean = false) => {

    return {
      onmouseover: (e: MouseEvent) => (e.target as HTMLElement)?.toggleAttribute('link-hover', true),
      onmouseout: (e: MouseEvent) => (e.target as HTMLElement)?.toggleAttribute('link-hover', false),
      onClick: (e: MouseEvent) => {
        if (
          e.button !== 0
          || e.ctrlKey
          || e.metaKey
          || e.shiftKey
        ) return;

        e.preventDefault();

        router.go(path, replace);
      }
    };
  };

  router.dispose = () => {

    window.removeEventListener('popstate', popStateHandler);
  };

  const popStateHandler = (e: PopStateEvent) => {
    router(e.state?.top);
  }

  window.addEventListener('popstate', popStateHandler);

  const emitRouting = (detail: boolean) => {

    return window.dispatchEvent(new CustomEvent<boolean>(
      'routing', { detail }
    ));
  }

  const emitRouteChanging = (detail: IRouteChangedDetail) => {

    return window.dispatchEvent(new CustomEvent<IRouteChangedDetail>(
      'routeChanging',
      {
        cancelable: true,
        detail
      }
    ));
  }

  const emitRouteChanged = (detail: IRouteChangedDetail) => {

    return window.dispatchEvent(
      new CustomEvent<IRouteChangedDetail>(
        'routeChanged', { detail }
      ));
  }

  router.renderFirst = (
    renderMap: { [name: string]: (match: IRouteMatch) => any; },
    notFound?: any
  ) => {
    if (state.matches) {

      const names = Object.keys(state.matches);
      if (names.length) {

        const name = names[0];
        if (renderMap[name])
          return renderMap[name](state.matches[name]);
      }
    }

    return typeof notFound === 'function'
      ? notFound()
      : notFound;
  }

  return router;
}
